import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { texts, useLang } from '../i18n.js';
import { Callout, Dialog, Loading, toast } from '../ui.js';

/**
 * Cancel, resume and remove for good (owner 2026-11-02, thread "Ta bort videor, granskningar och projekt"). All of it
 * sits under Fler val. Cancelling is the normal way (history and files stay, Resume brings it back); removing for good
 * needs an extra confirmation: the project's name typed, or a tick for a video. Monday's projects and videos are
 * cancelled in Monday, which the Hub follows.
 */

const TX = texts(
  {
    cancelMenu: 'Cancel the project…',
    removeMenu: 'Remove the project for good…',
    cancelTitle: (name: string) => `Cancel ${name}?`,
    cancelBody: 'The project leaves the project list, My work and time. Open reviews are withdrawn, so nobody is reminded. Notes, files and history stay, and you can resume it.',
    reason: 'Why? (optional)',
    reasonHint: 'Shown on the project and in its activity.',
    cancelShoots: 'Cancel the booked shoots too (the calendar invites are cancelled)',
    cancel: 'Cancel the project',
    keep: 'Keep it',
    cancelled: 'The project is cancelled',
    cancelledShoots: (n: number) => (n === 1 ? ' One shoot was cancelled.' : n ? ` ${n} shoots were cancelled.` : ''),
    bannerTitle: 'This project is cancelled',
    bannerReason: (r: string) => `Reason: ${r}`,
    resume: 'Resume',
    resumed: 'The project is back',
    removeTitle: (name: string) => `Remove ${name} for good?`,
    removeBody: (videos: number, reviews: number) =>
      `This removes the project, its ${videos === 1 ? 'video' : `${videos} videos`}${reviews ? `, ${reviews === 1 ? 'one review' : `${reviews} reviews`}` : ''}, shoots, notes and brief. It cannot be undone. Drive folders and files stay.`,
    removeShoots: (n: number) => (n === 1 ? 'The booked shoot is cancelled, and its calendar invite with it.' : `The ${n} booked shoots are cancelled, and their calendar invites with them.`),
    removeMonday: 'The project’s item on Monday stays: remove it there yourself.',
    typeName: (name: string) => `Type “${name}” to confirm`,
    remove: 'Remove for good',
    removed: (name: string) => `${name} is removed`,
    close: 'Close',
    removeVideoMenu: 'Remove for good…',
    removeVideoTitle: (name: string) => `Remove ${name} for good?`,
    removeVideoBody: 'The video, its reviews and its history are removed. It cannot be undone. Time logged on it stays on the project, and the files stay in Drive. To keep the history, choose Drop video… instead.',
    understand: 'I understand that this cannot be undone',
    removedVideo: (name: string) => `${name} is removed`
  },
  {
    cancelMenu: 'Avbryt projektet…',
    removeMenu: 'Ta bort projektet för gott…',
    cancelTitle: (name: string) => `Avbryta ${name}?`,
    cancelBody: 'Projektet försvinner från projektlistan, Mitt arbete och tid. Öppna granskningar dras tillbaka, så ingen får påminnelser. Anteckningar, filer och historik finns kvar, och du kan återuppta det.',
    reason: 'Varför? (valfritt)',
    reasonHint: 'Syns på projektet och i dess aktivitet.',
    cancelShoots: 'Ställ in de bokade inspelningarna också (kalenderinbjudan avbokas)',
    cancel: 'Avbryt projektet',
    keep: 'Behåll',
    cancelled: 'Projektet är avbrutet',
    cancelledShoots: (n: number) => (n === 1 ? ' En inspelning ställdes in.' : n ? ` ${n} inspelningar ställdes in.` : ''),
    bannerTitle: 'Projektet är avbrutet',
    bannerReason: (r: string) => `Anledning: ${r}`,
    resume: 'Återuppta',
    resumed: 'Projektet är tillbaka',
    removeTitle: (name: string) => `Ta bort ${name} för gott?`,
    removeBody: (videos: number, reviews: number) =>
      `Det här tar bort projektet, ${videos === 1 ? 'videon' : `${videos} videor`}${reviews ? `, ${reviews === 1 ? 'en granskning' : `${reviews} granskningar`}` : ''}, inspelningar, anteckningar och brief. Det går inte att ångra. Drive-mappar och filer finns kvar.`,
    removeShoots: (n: number) => (n === 1 ? 'Den bokade inspelningen ställs in, och kalenderinbjudan avbokas.' : `De ${n} bokade inspelningarna ställs in, och kalenderinbjudningarna avbokas.`),
    removeMonday: 'Projektets rad i Monday finns kvar: ta bort den där själv.',
    typeName: (name: string) => `Skriv ”${name}” för att bekräfta`,
    remove: 'Ta bort för gott',
    removed: (name: string) => `${name} är borttaget`,
    close: 'Stäng',
    removeVideoMenu: 'Ta bort för gott…',
    removeVideoTitle: (name: string) => `Ta bort ${name} för gott?`,
    removeVideoBody: 'Videon, dess granskningar och historik tas bort. Det går inte att ångra. Tid som loggats på den finns kvar på projektet, och filerna finns kvar i Drive. Vill du behålla historiken väljer du Stryk videon… i stället.',
    understand: 'Jag förstår att det inte går att ångra',
    removedVideo: (name: string) => `${name} är borttagen`
  }
);

export const removeTexts = TX;

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

interface Changed<P> {
  project: P;
  text: string;
  undoId?: string | null;
}

/** Cancel: an optional reason, and whether the booked shoots go too. */
export function CancelProjectDialog<P>(p: { api: Api; id: string; name: string; hasShoots: boolean; onClose: () => void; onDone: (r: Changed<P>) => void }) {
  const t = TX[useLang()];
  const [reason, setReason] = useState('');
  const [shoots, setShoots] = useState(p.hasShoots);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await p.api.post<Changed<P> & { cancelledShoots: number }>(`/api/projects/${p.id}/cancel`, { reason: reason.trim(), cancelShoots: shoots });
      p.onDone({ ...r, text: t.cancelled + '.' + t.cancelledShoots(r.cancelledShoots) });
      p.onClose();
    } catch (e) {
      toast({ text: errText(e) });
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t.cancelTitle(p.name)}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.keep}
          </button>
          <button type="button" class="btn btn-danger" disabled={busy} onClick={() => void run()}>
            {t.cancel}
          </button>
        </>
      }
    >
      <div class="stack">
        <p>{t.cancelBody}</p>
        <div class="field">
          <label class="label" for="cp-reason">
            {t.reason}
          </label>
          <textarea id="cp-reason" class="textarea" rows={2} maxLength={300} value={reason} onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)} aria-describedby="cp-reason-hint" />
          <span class="hint" id="cp-reason-hint">
            {t.reasonHint}
          </span>
        </div>
        {p.hasShoots ? (
          <label class="check">
            <input type="checkbox" checked={shoots} onChange={(e) => setShoots((e.currentTarget as HTMLInputElement).checked)} /> {t.cancelShoots}
          </label>
        ) : null}
      </div>
    </Dialog>
  );
}

interface RemoveInfo {
  name: string;
  blocker: string | null;
  videos: number;
  reviews: number;
  upcomingShoots: number;
  onMonday: boolean;
}

/** Remove for good: what goes with it, why it cannot (logged time, invoiced), and the project's name typed to confirm. */
export function RemoveProjectDialog(p: { api: Api; id: string; onClose: () => void; onRemoved: () => void }) {
  const t = TX[useLang()];
  const [info, setInfo] = useState<RemoveInfo | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    p.api.get<RemoveInfo>(`/api/projects/${p.id}/remove`).then(setInfo, (e) => (toast({ text: errText(e) }), p.onClose()));
  }, [p.id]);
  const ok = Boolean(info && !info.blocker && typed.trim() === info.name);
  const run = async () => {
    if (!info) return;
    setBusy(true);
    try {
      await p.api.post(`/api/projects/${p.id}/remove`, { confirm: typed.trim() });
      toast({ text: t.removed(info.name) });
      p.onRemoved();
    } catch (e) {
      toast({ text: errText(e) });
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={info ? t.removeTitle(info.name) : t.removeMenu.replace('…', '')}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {info?.blocker ? t.close : t.keep}
          </button>
          {info && !info.blocker ? (
            <button type="button" class="btn btn-danger-solid" disabled={!ok || busy} onClick={() => void run()}>
              {t.remove}
            </button>
          ) : null}
        </>
      }
    >
      {!info ? (
        <Loading />
      ) : info.blocker ? (
        <Callout tone="warn" title={info.blocker} />
      ) : (
        <div class="stack">
          <p>{t.removeBody(info.videos, info.reviews)}</p>
          {info.upcomingShoots ? <p>{t.removeShoots(info.upcomingShoots)}</p> : null}
          {info.onMonday ? <p>{t.removeMonday}</p> : null}
          <div class="field">
            <label class="label" for="rp-name">
              {t.typeName(info.name)}
            </label>
            <input id="rp-name" class="input input--wide" autocomplete="off" value={typed} onInput={(e) => setTyped((e.target as HTMLInputElement).value)} />
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** Remove a video for good: a tick that it cannot be undone, then the button. */
export function RemoveVideoDialog<P>(p: { api: Api; projectId: string; video: { id: string; name: string }; onClose: () => void; onDone: (r: Changed<P>) => void }) {
  const t = TX[useLang()];
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await p.api.post<Changed<P>>(`/api/projects/${p.projectId}/videos/${p.video.id}/remove`, { confirm: true });
      p.onDone({ ...r, text: t.removedVideo(p.video.name), undoId: null });
      p.onClose();
    } catch (e) {
      toast({ text: errText(e) });
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t.removeVideoTitle(p.video.name)}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.keep}
          </button>
          <button type="button" class="btn btn-danger-solid" disabled={!sure || busy} onClick={() => void run()}>
            {t.remove}
          </button>
        </>
      }
    >
      <div class="stack">
        <p>{t.removeVideoBody}</p>
        <label class="check">
          <input type="checkbox" checked={sure} onChange={(e) => setSure((e.currentTarget as HTMLInputElement).checked)} /> {t.understand}
        </label>
      </div>
    </Dialog>
  );
}

/** The cancelled project's banner on every tab: why, and Resume or Remove for good for the producer. */
export function CancelledBanner<P>(p: { api: Api; id: string; reason: string | null; canAct: boolean; onResumed: (r: Changed<P>) => void; onRemove: () => void }) {
  const t = TX[useLang()];
  const resume = async () => {
    try {
      const r = await p.api.post<Changed<P>>(`/api/projects/${p.id}/resume`);
      p.onResumed({ ...r, text: t.resumed });
    } catch (e) {
      toast({ text: errText(e) });
    }
  };
  return (
    <Callout tone="warn" title={t.bannerTitle}>
      {p.reason ? <p>{t.bannerReason(p.reason)}</p> : null}
      {p.canAct ? (
        <p class="cluster">
          <button type="button" class="btn btn-sm" onClick={() => void resume()}>
            {t.resume}
          </button>
          <button type="button" class="btn btn-sm btn-danger" onClick={p.onRemove}>
            {t.removeMenu}
          </button>
        </p>
      ) : null}
    </Callout>
  );
}
